import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, Gem, Receipt, TrendingUp, Scale, FileText, LogOut, Menu, X, Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PARTNER_LABELS } from "@/lib/format";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" },
    { to: "/parcels", label: "Parcels", icon: Package, testid: "nav-parcels" },
    { to: "/jewelry", label: "Jewelry", icon: Gem, testid: "nav-jewelry" },
    { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
    { to: "/sales", label: "Sales", icon: TrendingUp, testid: "nav-sales" },
    { to: "/ledger", label: "Ledger", icon: Scale, testid: "nav-ledger" },
    { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
];

export const Layout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
    const partnerLabel = user?.partner_key ? PARTNER_LABELS[user.partner_key] : null;

    return (
        <div className="min-h-screen bg-[#faf7ef] text-stone-900 flex">
            <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-stone-200 bg-[#faf7ef] sticky top-0 h-screen">
                <div className="px-6 py-6 border-b border-stone-200 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-emerald-700" strokeWidth={1.5} />
                    <div>
                        <div className="font-display text-xl leading-none tracking-tight">Parity</div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Diamond Ledger</div>
                    </div>
                </div>
                <nav className="flex-1 px-3 py-4 space-y-1">
                    {NAV.map(n => (
                        <NavLink key={n.to} to={n.to} end={n.exact} data-testid={n.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${isActive
                                    ? "bg-stone-200 text-stone-900 border-l-2 border-emerald-600 pl-[10px]"
                                    : "text-stone-500 hover:text-stone-900 hover:bg-stone-100 border-l-2 border-transparent"}`}>
                            <n.icon className="h-4 w-4" strokeWidth={1.5} />
                            <span>{n.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="px-3 py-4 border-t border-stone-200">
                    <button onClick={() => navigate("/settings")} data-testid="nav-settings"
                        className="w-full flex items-center gap-3 px-2 py-2 hover:bg-stone-100 text-left">
                        <Avatar className="h-8 w-8 rounded-none">
                            <AvatarImage src={user?.picture} />
                            <AvatarFallback className="rounded-none bg-stone-100 text-stone-700 text-xs">{initials}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                            <div className="text-xs truncate text-stone-800">{user?.name || user?.email}</div>
                            <div className={`text-[10px] uppercase tracking-widest ${partnerLabel ? "text-emerald-700" : "text-stone-500"}`} data-testid="user-partner-label">
                                {partnerLabel || "Viewer (read-only)"}
                            </div>
                        </div>
                    </button>
                    <Button variant="ghost" onClick={logout} data-testid="logout-button"
                        className="w-full justify-start text-stone-500 hover:text-stone-900 rounded-none mt-2">
                        <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign out
                    </Button>
                </div>
            </aside>

            <div className="md:hidden fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#faf7ef]/80 border-b border-stone-200">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-emerald-700" strokeWidth={1.5} />
                        <span className="font-display text-lg">Parity</span>
                    </div>
                    <button onClick={() => setOpen(v => !v)} data-testid="mobile-menu-toggle" className="p-2 text-stone-700">
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
                {open && (
                    <div className="border-t border-stone-200 px-2 py-2 space-y-1">
                        {NAV.map(n => (
                            <NavLink key={n.to} to={n.to} end={n.exact} onClick={() => setOpen(false)} data-testid={`mobile-${n.testid}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 text-sm ${isActive ? "bg-stone-100 text-stone-900" : "text-stone-700 hover:bg-stone-100"}`}>
                                <n.icon className="h-4 w-4" strokeWidth={1.5} />
                                {n.label}
                            </NavLink>
                        ))}
                        <button onClick={() => { setOpen(false); navigate("/settings"); }} data-testid="mobile-nav-settings"
                            className="w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-100">
                            <span className="h-4 w-4" /> Settings
                        </button>
                        <button onClick={logout} data-testid="mobile-logout-button"
                            className="w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-100">
                            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
                        </button>
                    </div>
                )}
            </div>

            <main className="flex-1 min-w-0 md:pt-0 pt-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default Layout;
